// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { MarketingAmbientBg } from "@/components/marketing";
import { getBrandAssets } from "@/lib/brandAssets";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { cn } from "@/lib/utils";

export interface EmberLoadingScreenProps {
  /** Full viewport vs centered in parent (e.g. dashboard content area) */
  layout?: "fullscreen" | "inline";
  /** Marketing grid + ambient for public pages; app uses shell tokens */
  variant?: "app" | "marketing";
  intensity?: "default" | "hero";
  showLabel?: boolean;
  label?: string;
  className?: string;
}

export function EmberLoadingScreen({
  layout = "fullscreen",
  variant = "app",
  intensity = "default",
  showLabel = true,
  label = "Loading",
  className,
}: EmberLoadingScreenProps) {
  const isMarketing = variant === "marketing";
  const brand = getBrandAssets("classic");
  const logoSrc = brand.logoMark;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-marketing={isMarketing ? "true" : undefined}
      className={cn(
        "relative flex flex-col items-center justify-center overflow-hidden",
        layout === "fullscreen" && "min-h-screen w-full",
        layout === "inline" && "min-h-[40vh] w-full py-12",
        isMarketing
          ? "marketing-grid-bg bg-marketing-canvas text-marketing-inkSecondary"
          : "app-loading-screen bg-background text-muted-foreground",
        className,
      )}
    >
      {isMarketing ? (
        <MarketingAmbientBg intensity={intensity} />
      ) : (
        <div aria-hidden="true" className="app-ambient pointer-events-none">
          <div className="app-ambient__orb app-ambient__orb--ember app-ambient__orb--animate-1" />
          <div className="app-ambient__orb app-ambient__orb--gold app-ambient__orb--animate-2" />
          <div className="app-ambient__orb app-ambient__orb--depth app-ambient__orb--animate-3" />
          <div className="app-ambient__mesh" />
        </div>
      )}

      <div className="relative z-[1] flex flex-col items-center gap-4">
        <div className="app-loading-screen__pulse-ring flex h-14 w-14 items-center justify-center rounded-full">
          <img
            src={logoSrc}
            alt=""
            aria-hidden="true"
            className="h-8 w-8 object-contain"
          />
        </div>
        {showLabel && (
          <span
            className={cn(
              "text-xs uppercase tracking-[0.18em]",
              isMarketing ? "text-marketing-inkMuted" : "text-muted-foreground",
            )}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

/** Full-screen Suspense fallback for public / auth routes. */
export function SuspenseEmberLoader() {
  const show = useDelayedLoading(true, 180);
  if (!show) return null;
  return <EmberLoadingScreen variant="app" layout="fullscreen" />;
}

/** In-shell Suspense fallback — keeps sidebar mounted during route transitions. */
export function SuspenseShellLoader() {
  const show = useDelayedLoading(true, 120);
  if (!show) return null;
  return <EmberLoadingScreen variant="app" layout="inline" />;
}
